/**
 * Organizations List Page
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformClient, Organization } from '../api/platformClient';
import { Building2, Clock, Users, ChevronRight, Plus, X, CheckCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

type Plan = 'free' | 'pro' | 'enterprise';

export default function OrganizationsPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: '',
    slug: '',
    plan: 'free' as Plan,
    ownerEmail: '',
    ownerPassword: '',
    ownerDisplayName: '',
  });

  const fetchOrgs = async () => {
    try {
      setIsLoading(true);
      const data = await platformClient.listOrganizations(1, 100);
      setOrganizations(data.organizations);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  const handleCreateOrganization = async () => {
    if (!newOrg.name || !newOrg.slug || !newOrg.ownerEmail || !newOrg.ownerPassword) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setIsCreating(true);
      setError('');
      const result = await platformClient.createOrganization({
        name: newOrg.name,
        slug: newOrg.slug,
        plan: newOrg.plan,
        ownerEmail: newOrg.ownerEmail,
        ownerPassword: newOrg.ownerPassword,
        ownerDisplayName: newOrg.ownerDisplayName || undefined,
      });
      setSuccessMessage(`Organization "${result.organization.name}" created successfully!`);
      setShowCreateModal(false);
      setNewOrg({ name: '', slug: '', plan: 'free', ownerEmail: '', ownerPassword: '', ownerDisplayName: '' });
      fetchOrgs();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create organization');
    } finally {
      setIsCreating(false);
    }
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  if (isLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Organizations</h1>
        <span style={styles.count}>{organizations.length} total</span>
        <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
          <Plus size={16} />
          Create Organization
        </button>
      </div>

      {/* Messages */}
      {successMessage && (
        <div style={styles.success}>
          <CheckCircle size={16} />
          <span>{successMessage}</span>
        </div>
      )}

      {error && !showCreateModal && (
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError('')} style={styles.dismissButton}>×</button>
        </div>
      )}

      <div style={styles.list}>
        {organizations.map((org) => (
          <div
            key={org.id}
            style={styles.orgCard}
            onClick={() => navigate(`/organizations/${org.id}`)}
          >
            <div style={styles.orgMain}>
              <div style={styles.orgIcon}>
                <Building2 size={24} color="#6366f1" />
              </div>
              <div style={styles.orgInfo}>
                <h3 style={styles.orgName}>{org.name}</h3>
                <span style={styles.orgSlug}>{org.slug}</span>
              </div>
              <span style={{
                ...styles.planBadge,
                backgroundColor: getPlanColor(org.plan),
              }}>
                {org.plan.toUpperCase()}
              </span>
            </div>

            <div style={styles.orgMeta}>
              <div style={styles.metaItem}>
                <Clock size={14} color="#64748b" />
                <span>Created {format(new Date(org.createdAt), 'MMM d, yyyy')}</span>
              </div>
              <div style={styles.metaItem}>
                <Users size={14} color="#64748b" />
                <span>{org.minutesUsed} / {org.maxMonthlyMinutes.toLocaleString()} min</span>
              </div>
            </div>

            <ChevronRight size={20} color="#64748b" style={styles.chevron} />
          </div>
        ))}

        {organizations.length === 0 && (
          <div style={styles.empty}>
            <Building2 size={48} color="#64748b" />
            <p>No organizations yet</p>
            <button onClick={() => setShowCreateModal(true)} style={styles.emptyButton}>
              Create First Organization
            </button>
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Create Organization</h3>
              <button onClick={() => setShowCreateModal(false)} style={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {error && (
                <div style={styles.modalError}>
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div style={styles.formSection}>
                <h4 style={styles.formSectionTitle}>Organization Details</h4>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Organization Name *</label>
                  <input
                    type="text"
                    value={newOrg.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setNewOrg({ ...newOrg, name, slug: generateSlug(name) });
                    }}
                    placeholder="Acme Corporation"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Slug *</label>
                  <input
                    type="text"
                    value={newOrg.slug}
                    onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="acme-corp"
                    style={styles.input}
                  />
                  <span style={styles.inputHint}>Lowercase letters, numbers, and hyphens only</span>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Plan</label>
                  <div style={styles.planOptions}>
                    {(['free', 'pro', 'enterprise'] as Plan[]).map((plan) => (
                      <button
                        key={plan}
                        type="button"
                        onClick={() => setNewOrg({ ...newOrg, plan })}
                        style={{
                          ...styles.planOption,
                          backgroundColor: newOrg.plan === plan ? '#6366f1' : '#334155',
                          borderColor: newOrg.plan === plan ? '#6366f1' : '#475569',
                        }}
                      >
                        {plan.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={styles.formSection}>
                <h4 style={styles.formSectionTitle}>Owner Account</h4>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Owner Email *</label>
                  <input
                    type="email"
                    value={newOrg.ownerEmail}
                    onChange={(e) => setNewOrg({ ...newOrg, ownerEmail: e.target.value })}
                    placeholder="admin@example.com"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Owner Password *</label>
                  <input
                    type="password"
                    value={newOrg.ownerPassword}
                    onChange={(e) => setNewOrg({ ...newOrg, ownerPassword: e.target.value })}
                    placeholder="••••••••"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Display Name</label>
                  <input
                    type="text"
                    value={newOrg.ownerDisplayName}
                    onChange={(e) => setNewOrg({ ...newOrg, ownerDisplayName: e.target.value })}
                    placeholder="John Doe"
                    style={styles.input}
                  />
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={styles.cancelButton}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrganization}
                style={styles.saveButton}
                disabled={isCreating || !newOrg.name || !newOrg.slug || !newOrg.ownerEmail || !newOrg.ownerPassword}
              >
                {isCreating ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getPlanColor(plan: string): string {
  switch (plan) {
    case 'free':
      return '#64748b';
    case 'pro':
      return '#6366f1';
    case 'enterprise':
      return '#22c55e';
    default:
      return '#64748b';
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '2rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2rem',
  },
  title: {
    color: '#f8fafc',
    fontSize: '1.5rem',
    fontWeight: 700,
    margin: 0,
  },
  count: {
    backgroundColor: '#334155',
    color: '#94a3b8',
    padding: '0.25rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.875rem',
  },
  loading: {
    color: '#94a3b8',
    padding: '2rem',
    textAlign: 'center',
  },
  error: {
    color: '#fecaca',
    backgroundColor: '#7f1d1d',
    padding: '1rem',
    borderRadius: '8px',
    margin: '2rem',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  orgCard: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '1.25rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    position: 'relative',
  },
  orgMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
  },
  orgIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: '#6366f120',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  orgInfo: {
    flex: 1,
  },
  orgName: {
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  },
  orgSlug: {
    color: '#64748b',
    fontSize: '0.875rem',
  },
  planBadge: {
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  orgMeta: {
    display: 'flex',
    gap: '1.5rem',
    paddingLeft: '64px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#94a3b8',
    fontSize: '0.875rem',
  },
  chevron: {
    position: 'absolute',
    right: '1.25rem',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  empty: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '3rem',
    textAlign: 'center',
    color: '#64748b',
  },
  emptyButton: {
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    marginTop: '1rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  createButton: {
    marginLeft: 'auto',
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  success: {
    backgroundColor: '#14532d',
    color: '#86efac',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  errorBanner: {
    backgroundColor: '#7f1d1d',
    color: '#fecaca',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  dismissButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'inherit',
    fontSize: '1.25rem',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '520px',
    margin: '1rem',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #334155',
    position: 'sticky',
    top: 0,
    backgroundColor: '#1e293b',
  },
  modalTitle: {
    color: '#f8fafc',
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: 0,
  },
  modalCloseButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '0.25rem',
    borderRadius: '4px',
  },
  modalBody: {
    padding: '1.5rem',
  },
  modalError: {
    backgroundColor: '#7f1d1d',
    color: '#fecaca',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
  },
  formSection: {
    marginBottom: '1.5rem',
  },
  formSectionTitle: {
    color: '#94a3b8',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0 0 1rem 0',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  formLabel: {
    color: '#94a3b8',
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    backgroundColor: '#334155',
    color: '#f8fafc',
    fontSize: '1rem',
  },
  inputHint: {
    display: 'block',
    marginTop: '0.25rem',
    fontSize: '0.75rem',
    color: '#64748b',
  },
  planOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.5rem',
  },
  planOption: {
    padding: '0.75rem',
    borderRadius: '8px',
    border: '2px solid',
    cursor: 'pointer',
    textAlign: 'center',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #334155',
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#1e293b',
  },
  cancelButton: {
    backgroundColor: '#475569',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
  },
  saveButton: {
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
